import React, { useState, useEffect, useRef } from "react";
import Card from "../components/Card";
import { C, TABLE } from "../constants";
import Badge from "../components/Badge";
import Btn from "../components/Btn";
import { PlanTable } from "./PlanTable";
import PlanVersionPanel from "./PlanVersionPanel";
import { generatePlan, refinePlan } from "../api";
import { useAppNotify } from "./AppNotify";
import * as XLSX from "xlsx";
import { fancyBaseControl } from "./FormControls";
import EmptyState from "./EmptyState";
import { dailySheetsForLayout, appendDailySheetsToWorkbook } from "../utils/weekScheduleGrid";
import { getDateForWeekDayFE, toDD } from "../utils";
import { loadTablePrefs, filterAoaByIndexes } from "../utils/tablePersonalize";
import { applyCurrentActivityTitles } from "../utils/activityExport";
import { visibleDailyBlueprint } from "../constants/dayActivities";

function applySheetColumnPrefs(sheets, layout) {
  const list = Array.isArray(sheets) ? sheets : [];
  if (layout === "dayList") {
    const prefs = loadTablePrefs("daily-list");
    const hidden = new Set(prefs.hidden || []);
    const labels = prefs.labels || {};
    const ids = ["time", "activity", "content"];
    const keep = ids.map((id, i) => (hidden.has(id) ? -1 : i)).filter((i) => i >= 0);
    if (!keep.length) return list;
    return list.map((s) => {
      const header = (s.header || []).map((h, i) => String(labels[ids[i]] || "").trim() || h);
      return {
        ...s,
        header: filterAoaByIndexes([header], keep)[0],
        body: filterAoaByIndexes(s.body || [], keep),
      };
    });
  }
  const prefs = loadTablePrefs("daily-grid");
  const hidden = new Set(prefs.hidden || []);
  const labels = prefs.labels || {};
  return list.map((s) => {
    const colIds = (s.columns || []).map((c) => c.id);
    const ids = ["day", ...colIds];
    const keep = ids.map((id, i) => (hidden.has(id) ? -1 : i)).filter((i) => i >= 0);
    if (!keep.length) return s;
    const header = (s.header || []).map((h, i) => {
      if (i === 0) return String(labels.day || "").trim() || h;
      const cid = colIds[i - 1];
      return String(labels[cid] || "").trim() || h;
    });
    return {
      ...s,
      header: filterAoaByIndexes([header], keep)[0],
      body: filterAoaByIndexes(s.body || [], keep),
    };
  });
}

function sanitizeFilePart(s) {
  return String(s || "plan")
    .trim()
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 48) || "plan";
}

function cellText(val) {
  return String(val ?? "").replace(/\\n/g, "\n");
}

// ── ModuleCard ─────────────────────────────────────────────────────────────────
export const ModuleCard = ({
  moduleKey, label, icon, plan, inputs, calMap, calendarMeta, onUpdate, onRestored, loading, onDayRegenerated,
  fileLabel = "",
  focusWeek = null, focusDay = null, focusToken = null,
  memberMeta = null,
  activityOverrides = null,
}) => {
  const { notify } = useAppNotify();
  const [feedback, setFeedback] = useState("");
  const [updating, setUpdating] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [useRefinement, setUseRefinement] = useState(true);
  const [histTick, setHistTick] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [dailyLayout, setDailyLayout] = useState("weekGrid");
  const latestPlanRef = useRef(plan);
  latestPlanRef.current = plan;

  // Reset local undo/redo when a new plan is loaded / regenerated
  useEffect(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, [plan?.module, plan?.rows?.length, moduleKey]);

  const fileBase = sanitizeFilePart(fileLabel || moduleKey);

  const pushPlanChange = (next) => {
    if (!onUpdate) return;
    if (plan) {
      setUndoStack((s) => [...s.slice(-39), JSON.parse(JSON.stringify(plan))]);
      setRedoStack([]);
    }
    onUpdate(moduleKey, next);
  };

  const undoEdit = () => {
    if (!undoStack.length || !onUpdate) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    if (plan) setRedoStack((s) => [...s, JSON.parse(JSON.stringify(plan))]);
    onUpdate(moduleKey, prev);
  };

  const redoEdit = () => {
    if (!redoStack.length || !onUpdate) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack((s) => s.slice(0, -1));
    if (plan) setUndoStack((s) => [...s, JSON.parse(JSON.stringify(plan))]);
    onUpdate(moduleKey, next);
  };

  const downloadXLSX = () => {
    const exportPlan = latestPlanRef.current;
    if (!exportPlan) return;
    const wb = XLSX.utils.book_new();
    if (exportPlan.module === "daily") {
      const sheets = applySheetColumnPrefs(
        dailySheetsForLayout(
          dailyLayout,
          dailyLayout === "dayList" ? applyCurrentActivityTitles(exportPlan.rows || [], activityOverrides) : (exportPlan.rows || []),
          calMap,
          (map, week, day) => {
            const iso = getDateForWeekDayFE(map, week, day);
            return iso ? toDD(iso) : null;
          },
          visibleDailyBlueprint(activityOverrides || {}),
          true
        ),
        dailyLayout
      );
      if (!sheets.length) return;
      appendDailySheetsToWorkbook(wb, XLSX, sheets);
    } else {
      const safeCols = Array.isArray(exportPlan.columns) && exportPlan.columns.length > 0
        ? exportPlan.columns
        : (exportPlan.rows[0] || []).map((_, i) => ["Week", "Days", "Subjects Covered", "Project Milestone", "Exam Focus"][i] || `Col ${i + 1}`);
      const prefs = loadTablePrefs("weekly");
      const hidden = new Set(prefs.hidden || []);
      const labels = prefs.labels || {};
      const labeledCols = safeCols.map((name, i) => String(labels[`c${i}`] || "").trim() || name);
      const keep = labeledCols.map((_, i) => (hidden.has(`c${i}`) ? -1 : i)).filter((i) => i >= 0);
      const useKeep = keep.length ? keep : labeledCols.map((_, i) => i);
      const header = filterAoaByIndexes([labeledCols], useKeep)[0];
      const body = filterAoaByIndexes(
        (exportPlan.rows || []).map((row) => (row || []).map((c) => cellText(c))),
        useKeep
      );
      const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
      ws["!cols"] = header.map((_, i) => {
        const src = safeCols[useKeep[i]] || "";
        if (useKeep[i] === 0) return { wch: 14 };
        if (useKeep[i] === 1) return { wch: 12 };
        if (/milestone/i.test(String(src))) return { wch: 55 };
        if (/exam/i.test(String(src))) return { wch: 40 };
        if (/subject/i.test(String(src))) return { wch: 40 };
        return { wch: 28 };
      });
      const milestoneIdx = header.findIndex((c) => /milestone/i.test(String(c || "")));
      ws["!rows"] = [{ hpt: 22 }, ...body.map((row) => {
        const text = milestoneIdx >= 0 ? String(row[milestoneIdx] || "") : row.map(String).join("\n");
        const lines = Math.max(4, text.split(/\n/).length);
        return { hpt: Math.min(240, 14 * lines + 12) };
      })];
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let R = 1; R <= range.e.r; R++) {
        for (let C = 0; C <= range.e.c; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[addr]) ws[addr].s = { alignment: { wrapText: true, vertical: "top" } };
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, sanitizeFilePart(fileLabel || label).slice(0, 31) || "Weekly");
    }
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    XLSX.writeFile(wb, `${fileBase}_${moduleKey}_plan_${stamp}.xlsx`);
  };

  const downloadCSV = () => {
    const exportPlan = latestPlanRef.current;
    if (!exportPlan) return;

    const escCell = (val) =>
      `"${cellText(val).replace(/"/g, '""')}"`;

    let csvContent = "";
    if (exportPlan.module === "daily") {
      const sheets = applySheetColumnPrefs(
        dailySheetsForLayout(
          dailyLayout,
          dailyLayout === "dayList" ? applyCurrentActivityTitles(exportPlan.rows || [], activityOverrides) : (exportPlan.rows || []),
          calMap,
          (map, week, day) => {
            const iso = getDateForWeekDayFE(map, week, day);
            return iso ? toDD(iso) : null;
          },
          visibleDailyBlueprint(activityOverrides || {}),
          true
        ),
        dailyLayout
      );
      sheets.forEach(({ sheetName, header, body }, idx) => {
        if (idx > 0) csvContent += "\r\n";
        csvContent += escCell(sheetName) + "\r\n";
        csvContent += header.map(escCell).join(",") + "\r\n";
        body.forEach((row) => {
          csvContent += row.map(escCell).join(",") + "\r\n";
        });
      });
    } else {
    const safeCols =
      Array.isArray(exportPlan.columns) && exportPlan.columns.length > 0
        ? exportPlan.columns
        : (exportPlan.rows[0] || []).map(
            (_, i) =>
              ["Week", "Days", "Subjects Covered", "Project Milestone", "Exam Focus"][i] ||
              `Col ${i + 1}`
          );
    const prefs = loadTablePrefs("weekly");
    const hidden = new Set(prefs.hidden || []);
    const labels = prefs.labels || {};
    const labeledCols = safeCols.map((name, i) => String(labels[`c${i}`] || "").trim() || name);
    const keep = labeledCols.map((_, i) => (hidden.has(`c${i}`) ? -1 : i)).filter((i) => i >= 0);
    const useKeep = keep.length ? keep : labeledCols.map((_, i) => i);
    const header = filterAoaByIndexes([labeledCols], useKeep)[0];
    const body = filterAoaByIndexes(
      (exportPlan.rows || []).map((row) => (row || []).map((c) => cellText(c))),
      useKeep
    );

    csvContent += header.map(escCell).join(",") + "\r\n";
    body.forEach((row) => {
      csvContent += row.map(escCell).join(",") + "\r\n";
    });
    }

    const blob = new Blob(
      ["\uFEFF" + csvContent],
      { type: "text/csv;charset=utf-8;" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.download = `${fileBase}_${moduleKey}_plan_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUpdate = async () => {
    if (!feedback.trim() || !plan) return;
    setUpdating(true);
    try {
      let res;
      if (useRefinement || moduleKey === "weekly") {
        const prompt = feedback.trim() || "Refresh this weekly plan while keeping the same subjects, weekly topics, and overall structure.";
        res = await refinePlan(moduleKey, plan, prompt);
        if (res.success) {
          // Parent persists + saves a version (avoids double-save)
          onUpdate(moduleKey, res.plan, {
            changeType: "Refined",
            reason: `Refinement (${moduleKey}): ${prompt}`,
          });
          setHistTick((t) => t + 1);
        }
      } else {
        res = await generatePlan(moduleKey, inputs, feedback.trim());
        if (res.success) {
          onUpdate(moduleKey, res.plan, {
            changeType: "Regenerated",
            reason: `Regeneration (${moduleKey}): ${feedback.trim()}`,
          });
          setHistTick((t) => t + 1);
        }
      }
      setFeedback("");
    } catch (e) {
      notify({ type: "error", title: "Update failed", message: e.message || "Update failed" });
    }
    setUpdating(false);
  };

  return (
    <Card style={{ animation: "fadeIn 0.3s ease" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, background: C.tagBg,
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <i className={`ti ${icon}`} style={{ fontSize: 20, color: C.primary }} />
          </div>
          <div>
            <p style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{label}</p>
            <p style={{ fontSize: 12, color: C.muted }}>
              {plan ? `${plan.rows?.length || 0} rows generated` : "Not generated"}
            </p>
          </div>
        </div>
        {plan ? <Badge bg={C.successBg} color={C.success}>✓ Ready</Badge>
          : loading ? <Badge bg={C.warningBg} color={C.warning}>Generating...</Badge>
            : <Badge bg={C.bg} color={C.muted}>Pending</Badge>}
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "3rem 0" }}>
          <div style={{
            width: 36, height: 36, border: `3px solid ${C.border}`,
            borderTopColor: C.primary, borderRadius: "50%", margin: "0 auto 12px",
            animation: "spin 0.8s linear infinite"
          }} />
          <p style={{ color: C.muted, fontSize: 14 }}>Generating schedule...</p>
          {moduleKey === "daily" && (
            <p style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
              takes ~3–5 min
            </p>
          )}
        </div>
      )}

      {!loading && !plan && (
        <EmptyState
          icon="ti-calendar-plus"
          title="No plan yet"
          body="Add a problem statement, then open Review and generate. Timing and modules are optional."
        />
      )}

      {plan && !loading && (
        <>
          {(moduleKey === "daily" || moduleKey === "weekly") && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 10, flexWrap: "wrap", marginBottom: 10,
              padding: "10px 12px", borderRadius: 8,
              background: editMode ? TABLE.editBanner : C.tagBg || "#F3F4F6",
              border: `1px solid ${editMode ? "#93C5FD" : C.border}`,
            }}>
              <div style={{ fontSize: 12.5, color: C.text, flex: 1, minWidth: 180 }}>
                <strong>{editMode ? "Edit mode ON" : "Need a correction?"}</strong>
                {" "}
                {editMode
                  ? "Click any task / cell, change text, then Save. Use Undo / Redo to restore."
                  : "Use Edit schedule here (one place) to fix lines without regenerating."}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <Btn small variant="ghost" disabled={!undoStack.length} onClick={undoEdit} title="Undo last edit">
                  <i className="ti ti-arrow-back-up" style={{ fontSize: 14 }} /> Undo
                </Btn>
                <Btn small variant="ghost" disabled={!redoStack.length} onClick={redoEdit} title="Redo">
                  <i className="ti ti-arrow-forward-up" style={{ fontSize: 14 }} /> Redo
                </Btn>
                <Btn
                  small
                  variant={editMode ? "primary" : "outline"}
                  onClick={() => setEditMode((v) => !v)}
                >
                  <i className="ti ti-pencil" style={{ fontSize: 14 }} />
                  {editMode ? "Done editing" : "Edit schedule"}
                </Btn>
              </div>
            </div>
          )}
          <div style={{ marginBottom: 14, borderRadius: 8 }}>
            <PlanTable
              plan={plan}
              moduleKey={moduleKey}
              calMap={calMap}
              calendarMeta={calendarMeta}
              onDayRegenerated={onDayRegenerated}
              editMode={editMode}
              onPlanChange={pushPlanChange}
              focusWeek={focusWeek}
              focusDay={focusDay}
              focusToken={focusToken}
              memberMeta={memberMeta}
              scheduleLayout={dailyLayout}
              onScheduleLayoutChange={setDailyLayout}
            />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            <Btn small variant="outline" onClick={downloadXLSX}>
              <i className="ti ti-file-spreadsheet" style={{ fontSize: 14 }} />
              {moduleKey === "daily"
                ? (dailyLayout === "dayList" ? "Excel (day list)" : "Excel (week table)")
                : "Excel"}
            </Btn>
            <Btn small variant="ghost" onClick={downloadCSV}>
              <i className="ti ti-download" style={{ fontSize: 14 }} /> CSV
            </Btn>
            <Btn small variant="ghost" onClick={() => setShowHist(!showHist)}>
              <i className="ti ti-history" style={{ fontSize: 14 }} />
              History
            </Btn>
          </div>

          <div style={{ marginBottom: 12, padding: "8px 12px", background: C.tagBg, borderRadius: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={useRefinement} onChange={e => setUseRefinement(e.target.checked)}
                style={{ width: 16, height: 16, cursor: "pointer" }} />
              <span><strong>Smart Refinement Mode</strong> — Keep subjects/topics, only enhance clarity</span>
            </label>
            {!useRefinement && (
              <p style={{ fontSize: 11, color: C.warning, marginTop: 6, marginBottom: 0 }}>
                ⚠️ Full regeneration will create an entirely new plan (subjects may change)
              </p>
            )}
          </div>

          <PlanVersionPanel
            key={histTick}
            open={showHist}
            onRestored={(restoredPlan, newVersion) => {
              onRestored?.(restoredPlan, newVersion);
            }}
          />
        </>
      )}

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 4 }}>
          <i className="ti ti-sparkles" style={{ fontSize: 13, marginRight: 4, color: C.primary }} />
          {useRefinement ? "Additional Instructions (refine current plan)" : "Additional Instructions (regenerate)"}
        </p>
        <p style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.4 }}>
          {useRefinement
            ? "Updates this plan now. Your feedback is also saved as a lasting rule so new plans will not repeat the same mistake."
            : "Builds a new plan. Your feedback is saved as a lasting rule for this and future plans."}
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={feedback} onChange={e => setFeedback(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleUpdate()}
            disabled={!plan || updating}
            placeholder={useRefinement ?
              'e.g. "Link every case study to the problem statement"' :
              'e.g. "Change focus to practical implementation"'}
            style={{
              flex: 1,
              ...fancyBaseControl,
              border: `1px solid ${C.borderStrong}`,
              background: plan ? C.surface : C.bg,
            }} />
          <Btn onClick={handleUpdate} disabled={!plan || updating || !feedback.trim()} small>
            {updating ? "..." : useRefinement ? "Refine →" : "Regenerate →"}
          </Btn>
        </div>
      </div>
    </Card>
  );
}
